import React, { ChangeEvent, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import { MomentOccasion } from "../../../../api/moments";
import { KlydoType } from "../../../../api/klydo";
import useKlydo from "../../../../components/hooks/useKlydo";
import { extractApiErrorMessage } from "../../../../utils/util";
import KlydosPickDialog from "../../../Gifts/GiftCategories/components/KlydosPickDialog";
import DSAlert from "../../../system-design/DSAlert";
import DSBox from "../../../system-design/DSBox";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSEnumSelect from "../../../system-design/DSEnumSelect";
import DSHint from "../../../system-design/DSHint";
import { MOMENT_ICON_OPTIONS } from "../momentIcons";
import DSFormControlLabel from "../../../system-design/DSFormControlLabel";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import DSStack from "../../../system-design/DSStack";
import DSSwitch from "../../../system-design/DSSwitch";
import DSTextField from "../../../system-design/DSTextField";
import DSTypography from "../../../system-design/DSTypography";
import DSUploadButton from "../../../system-design/DSUploadButton";
import useSaveMomentOccasionMutation from "../hooks/useSaveMomentOccasionMutation";

const KEY_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const OccasionKlydo = ({
  klydoId,
  onRemove,
}: {
  klydoId: string;
  onRemove: (klydoId: string) => void;
}) => {
  const { data: klydo } = useKlydo(klydoId);
  return (
    <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <DSTypography sx={{ flex: 1 }} title={klydoId}>
        {klydo?.name ?? klydoId}
      </DSTypography>
      <IconButton size="small" onClick={() => onRemove(klydoId)}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </DSBox>
  );
};

// Create (no `row`) or edit one occasion of the Moments whitelist. Rendered
// inside the table's dialog for edits and inside its own dialog for "Add".
const MomentOccasionModal = (props: {
  row?: MomentOccasion;
  setShowModal: (show: boolean) => void;
}) => {
  const { row, setShowModal } = props;
  const isNew = !row;
  const { mutate: save, isPending, error } = useSaveMomentOccasionMutation();

  const [key, setKey] = useState(row?.key ?? "");
  const [label, setLabel] = useState(row?.label ?? "");
  const [icon, setIcon] = useState(row?.icon ?? "");
  const [searchPhrase, setSearchPhrase] = useState(row?.searchPhrase ?? "");
  const [active, setActive] = useState(row?.active ?? true);
  const [order, setOrder] = useState(row?.order ?? 0);
  const [klydoIds, setKlydoIds] = useState<string[]>(row?.klydoIds ?? []);
  const [logoFile, setLogoFile] = useState<string>();
  const [logoName, setLogoName] = useState<string>();
  const [showPicker, setShowPicker] = useState(false);

  // An icon stored earlier that is not in the list stays selectable.
  const iconOptions =
    MOMENT_ICON_OPTIONS.some((o) => o.value === icon) || !icon
      ? MOMENT_ICON_OPTIONS
      : [
          {
            value: icon,
            label: icon,
            Preview:
              MOMENT_ICON_OPTIONS[MOMENT_ICON_OPTIONS.length - 1].Preview,
          },
          ...MOMENT_ICON_OPTIONS,
        ];

  const keyIsValid = !isNew || KEY_PATTERN.test(key);
  const canSave =
    keyIsValid &&
    !!label.trim() &&
    !!icon.trim() &&
    !!searchPhrase.trim() &&
    klydoIds.length > 0;

  const onPickLogo = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setLogoFile(reader.result as string);
      setLogoName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const onSave = () => {
    const common = {
      label: label.trim(),
      icon: icon.trim(),
      searchPhrase: searchPhrase.trim(),
      active,
      order,
      klydoIds,
    };
    const onSuccess = () => setShowModal(false);
    if (isNew) {
      save(
        { isNew: true, payload: { key, ...common }, logoFile },
        { onSuccess },
      );
    } else {
      save(
        { isNew: false, key: row.key, payload: common, logoFile },
        { onSuccess },
      );
    }
  };

  return (
    <>
      <DSDialogTitle>
        {isNew ? "Add occasion" : `Edit occasion: ${row.label}`}
      </DSDialogTitle>
      <DSDialogContent>
        {error && (
          <DSAlert severity="error" sx={{ mb: 2 }}>
            {extractApiErrorMessage(error, "Failed to save the occasion")}
          </DSAlert>
        )}
        <DSStack spacing={2} sx={{ mt: 1 }}>
          <DSBox>
            <DSHint label="Key">
              Lowercase letters, digits and dashes, e.g. <b>birthday</b>. It
              identifies the occasion and cannot be changed later.
            </DSHint>
            <DSTextField
              placeholder="birthday"
              error={isNew && !!key && !keyIsValid}
              disabled={!isNew}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              fullWidth
            />
          </DSBox>
          <DSBox>
            <DSHint label="Label">
              The name users see in the occasion picker and next to their
              moment, e.g. Birthday. Up to 30 characters.
            </DSHint>
            <DSTextField
              placeholder="Birthday"
              value={label}
              inputProps={{ maxLength: 30 }}
              onChange={(e) => setLabel(e.target.value)}
              fullWidth
            />
          </DSBox>
          <DSBox>
            <DSHint label="App icon">
              Shown next to this occasion in the app: in the picker and in the
              user&apos;s list of moments.
            </DSHint>
            <DSEnumSelect
              value={icon}
              displayEmpty
              fullWidth
              options={iconOptions.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              renderValue={(value) => {
                const option = iconOptions.find((o) => o.value === value);
                if (!option) return "Choose an icon";
                return (
                  <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <option.Preview />
                    {option.label}
                  </DSBox>
                );
              }}
              onChange={setIcon}
            />
          </DSBox>
          <DSBox>
            <DSHint label="Search phrase">
              Describe what the klydo and the pendulum art should look like:
              5–12 concrete visual words, comma-separated, in English. Example:{" "}
              <i>birthday celebration, cake with candles, balloons, confetti</i>
              .
              <br />
              It is used (1) as the theme of the AI pendulum art and (2) to
              search for a klydo when the Moments page is set to semantic
              search. Never names or personal text.
            </DSHint>
            <DSTextField
              placeholder="birthday celebration, cake with candles, balloons"
              value={searchPhrase}
              inputProps={{ maxLength: 100 }}
              onChange={(e) => setSearchPhrase(e.target.value)}
              fullWidth
            />
          </DSBox>
          <DSBox>
            <DSHint label="Order">
              Position in the app&apos;s occasion picker: the lowest number
              comes first.
            </DSHint>
            <DSTextField
              type="number"
              value={order}
              onChange={(e) => setOrder(parseInt(e.target.value, 10) || 0)}
            />
          </DSBox>
          <DSFormControlLabel
            control={
              <DSSwitch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            }
            label="Active (shown in the app)"
          />

          <DSBox>
            <DSHint label="Default pendulum logo">
              A transparent PNG, 200–380 px, ideally a single-colour glyph (the
              clock tints it with its theme colour). It is the fallback when the
              personalised picture cannot be generated, and what clocks with
              older firmware show.
            </DSHint>
            {row?.logoUrl && !logoFile && (
              <img
                src={row.logoUrl}
                alt="Current logo"
                width={80}
                style={{ background: "#333", borderRadius: 8, padding: 4 }}
              />
            )}
            <label style={{ display: "block", marginTop: 8 }}>
              <input
                type="file"
                accept="image/png"
                hidden
                onChange={onPickLogo}
              />
              <DSUploadButton
                text={logoName ? `Selected: ${logoName}` : "Upload logo"}
              />
            </label>
          </DSBox>

          <DSBox>
            <DSBox sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <DSBox sx={{ flex: 1 }}>
                <DSHint label={`Klydos (${klydoIds.length})`}>
                  <b>At least one is required.</b> One of these is picked at
                  random for each moment, and it is the default when the
                  semantic search finds nothing. Use at least 10 public, sealed
                  klydos with no 13+/PSF tags and no sound, so a clock with
                  several moments of this occasion gets a different one.
                </DSHint>
              </DSBox>
              <DSSecondaryButton onClick={() => setShowPicker(true)}>
                Choose klydos
              </DSSecondaryButton>
            </DSBox>
            {klydoIds.map((klydoId) => (
              <OccasionKlydo
                key={klydoId}
                klydoId={klydoId}
                onRemove={(id) =>
                  setKlydoIds((ids) => ids.filter((x) => x !== id))
                }
              />
            ))}
          </DSBox>
        </DSStack>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={() => setShowModal(false)}>
          Cancel
        </DSSecondaryButton>
        <DSPrimaryButton disabled={!canSave || isPending} onClick={onSave}>
          {isNew ? "Add occasion" : "Save"}
        </DSPrimaryButton>
      </DSDialogActions>
      {showPicker && (
        <KlydosPickDialog
          selectedKlydos={klydoIds}
          onPickKlydos={(ids) => {
            setKlydoIds(ids);
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
          publicOnly
          klydoType={KlydoType.Sealed}
          title="Klydos for this occasion"
        />
      )}
    </>
  );
};

export default MomentOccasionModal;
